import Fade from '@mui/material/Fade';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../../theme';
import Loader from '../../Loader/loader';
import LinkTab from '../../Settings/linkTab';
import TabPanel from '../../Settings/TabPanel/tabPanel';
import { SurveyDefaultDatePeriod, SurveyForConfigurationProps, SurveyStatus, SurveyTemporalFilterType } from '../../Surveys/surveyInterface';
import Title from '../../Title/title';
import { displayToast } from '../../Toaster/displayToast';
import { CloseIconModalStyled, ModalStyled } from '../DashboardModal/styled';
import { ConfiguratedHotAlerts, PersonalizedSurveyProps, SelectedQuestion, SelectedSurveyProps } from './addSurveyInterface';
import loadSurveyForEdition from './loadSurveyForEdition';
import StepConfigureHotAlerts from './Steps/StepConfigureHotAlerts';
import StepDeployment from './Steps/StepDeployment';
import StepPersonalizeSurvey from './Steps/StepPersonalizeSurvey';
import StepSelectFilters from './Steps/StepSelectFilters';
import StepSelectQuestion from './Steps/StepSelectQuestion';
import StepSelectSurvey from './Steps/StepSelectSurvey';
import { TabsSurveyCreationStyled, BoxModalStyled, LoaderContentStyled } from './styled';

interface createSurveyModalInterface {
    handleClose: () => void
    openModal: boolean
    // When set, the modal edits this survey: step 1 (Askia survey selection) is locked
    surveyToEdit?: SurveyForConfigurationProps
}

const CreateSurvey: FC<createSurveyModalInterface> = ({ handleClose, openModal, surveyToEdit }) => {
    const isEditMode = surveyToEdit !== undefined;
    const [currentContent, setCurrentContent] = useState(isEditMode ? '#step2' : '#step1');
    const { t } = useTranslation('settings');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const [isLoadingSurvey, setIsLoadingSurvey] = useState<boolean>(isEditMode);
    const [hotAlertConfigId, setHotAlertConfigId] = useState<number | undefined>(undefined);
    const [selectedSurvey, setSelectedSurvey] = useState<SelectedSurveyProps | undefined>(undefined);
    const [personalizedSurvey, setPersonalizedSurvey] = useState<PersonalizedSurveyProps | undefined>(undefined);
    const [selectedQuestions, setSelectedQuestions] = useState<SelectedQuestion[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<SelectedQuestion[]>([]);
    const [configuratedHotAlerts, setConfiguratedHotAlerts] = useState<ConfiguratedHotAlerts | undefined>(undefined);

    useEffect(() => {
        if (!surveyToEdit) { return undefined; }
        let isCancelled = false;
        loadSurveyForEdition(surveyToEdit, selectedSchema || '')
            .then((edition) => {
                if (isCancelled) { return; }
                setSelectedSurvey(edition.selectedSurvey);
                setPersonalizedSurvey(edition.personalizedSurvey);
                setSelectedQuestions(edition.selectedQuestions);
                setSelectedFilters(edition.selectedFilters);
                setConfiguratedHotAlerts(edition.configuratedHotAlerts);
                setHotAlertConfigId(edition.hotAlertConfigId);
                setIsLoadingSurvey(false);
            })
            .catch(() => {
                if (isCancelled) { return; }
                displayToast(t('tabs.createSurvey.errorLoadingSurvey'), 'error');
                handleClose();
            });

        return () => { isCancelled = true; };
    }, [surveyToEdit]);

    const handleChange = (event: React.SyntheticEvent, newValue: string): void => {
        setCurrentContent(newValue);
    };
    const handleSelectedSurvey = (survey: SelectedSurveyProps) => {
        setSelectedSurvey(survey);
        const personalizedSurveyToSet: PersonalizedSurveyProps = {
            id: survey.id,
            shortName: survey.name,
            name: survey.description,
            display: true,
            status: SurveyStatus.IN_PREPARATION,
            updateDate: new Date(),
            schema: '',
            temporalFilterType: SurveyTemporalFilterType.VAGUE,
            defaultDatePeriod: SurveyDefaultDatePeriod.CURRENT_MONTH,
            context: '',
            hasDashboard: true,
            hasHotAlert: false,
            hasToAnonymateData: false,
            anonymateDays: undefined,
            hasAutomaticSynchronization: false,
        };
        setPersonalizedSurvey(personalizedSurveyToSet);
        setSelectedQuestions([]);
        setSelectedFilters([]);
        setCurrentContent('#step2');
    };
    const checkForm = (formId: string, displayError: boolean) => {
        switch (formId) {
            case '#step2':
                if (!personalizedSurvey || !personalizedSurvey.name || !personalizedSurvey.context || !personalizedSurvey.schema || personalizedSurvey.hasDashboard === undefined || personalizedSurvey.hasHotAlert === undefined || personalizedSurvey.hasToAnonymateData === undefined || (personalizedSurvey.hasToAnonymateData && !personalizedSurvey.anonymateDays)) {
                    if (displayError) {
                        displayToast(t('tabs.createSurvey.nextStepError'), 'error');
                    }

                    return false;
                }
                break;
            case '#step3':
                if (!selectedQuestions || selectedQuestions.length === 0) {
                    if (displayError) {
                        displayToast(t('tabs.createSurvey.step3Content.nextStepError'), 'error');
                    }

                    return false;
                }
                break;
            case '#step4':
                if (!selectedQuestions || selectedQuestions.length === 0) {
                    if (displayError) {
                        displayToast(t('tabs.createSurvey.step3Content.nextStepError'), 'error');
                    }

                    return false;
                }
                break;
            case '#step5': {
                const canPassWithHotAlert = personalizedSurvey && personalizedSurvey.hasHotAlert && selectedQuestions.length > 0 && configuratedHotAlerts && (configuratedHotAlerts.statuses.length === 0 || (configuratedHotAlerts.statuses.some((s) => s.isDefault) && configuratedHotAlerts.statuses.some((s) => s.isFinal))) && (!configuratedHotAlerts.satisfaction || (configuratedHotAlerts.satisfaction && (configuratedHotAlerts.satisfaction.positiveModalityIds.length > 0 || configuratedHotAlerts.satisfaction.negativeModalityIds.length > 0)));
                const canPassWithoutHotAlert = personalizedSurvey && !personalizedSurvey.hasHotAlert && selectedQuestions.length > 0;
                if (!canPassWithHotAlert && !canPassWithoutHotAlert) {
                    if (displayError) {
                        displayToast(t('tabs.createSurvey.step5Content.nextStepError'), 'error');
                    }

                    return false;
                }
                break;
            }
            default:
                break;
        }

        return true;
    };
    const handleNextStep = (formId: string) => {
        switch (formId) {
            case '#step2':
                if (checkForm(formId, true)) {
                    setCurrentContent('#step3');
                }
                break;
            case '#step3':
                if (checkForm(formId, true)) {
                    setCurrentContent('#step4');
                }
                break;
            case '#step4':
                if (checkForm(formId, true)) {
                    if (personalizedSurvey && personalizedSurvey.hasHotAlert) {
                        setCurrentContent('#step5');
                    } else {
                        setCurrentContent('#step6');
                    }
                }
                break;
            case '#step5':
                if (checkForm(formId, true)) {
                    setCurrentContent('#step6');
                }
                break;
            default:
                break;
        }
    };

    return (
        <ModalStyled
            aria-describedby="modal-modal-description"
            aria-labelledby="modal-modal-title"
            onClose={handleClose}
            open={openModal}
        >
            <Fade in={openModal}>
                <BoxModalStyled>
                    <Title content={isEditMode ? t('tabs.createSurvey.editTitle', { surveyName: surveyToEdit.name }) : t('tabs.createSurvey.title')} />
                    <CloseIconModalStyled onClick={handleClose} />
                    {isLoadingSurvey ? (
                        <LoaderContentStyled>
                            <Loader size={14} />
                            <TypographyStyledGlobal color={colors.grey200} size={fontSize.sm} weight={fontWeight.light}>
                                {t('tabs.createSurvey.loadingSurvey')}
                            </TypographyStyledGlobal>
                        </LoaderContentStyled>
                    ) : (
                        <>
                            <TabsSurveyCreationStyled onChange={handleChange} value={currentContent}>
                                <LinkTab currentContent={currentContent} disabled={isEditMode} label={t('tabs.createSurvey.step1')} setCurrentContent={setCurrentContent} value="#step1" disableRipple wrapped />
                                <LinkTab currentContent={currentContent} disabled={!selectedSurvey && !personalizedSurvey} label={t('tabs.createSurvey.step2')} setCurrentContent={setCurrentContent} value="#step2" disableRipple wrapped />
                                <LinkTab currentContent={currentContent} disabled={!checkForm('#step2', false)} label={t('tabs.createSurvey.step3')} setCurrentContent={setCurrentContent} value="#step3" disableRipple wrapped />
                                <LinkTab currentContent={currentContent} disabled={!checkForm('#step3', false)} label={t('tabs.createSurvey.step4')} setCurrentContent={setCurrentContent} value="#step4" disableRipple wrapped />
                                <LinkTab currentContent={currentContent} disabled={!selectedQuestions || selectedQuestions.length === 0 || !personalizedSurvey || !personalizedSurvey.hasHotAlert} label={t('tabs.createSurvey.step5')} setCurrentContent={setCurrentContent} value="#step5" disableRipple wrapped />
                                <LinkTab currentContent={currentContent} disabled={!checkForm('#step5', false)} label={isEditMode ? t('tabs.createSurvey.step6Edit') : t('tabs.createSurvey.step6')} setCurrentContent={setCurrentContent} value="#step6" disableRipple wrapped />
                            </TabsSurveyCreationStyled>
                            <TabPanel index='#step1' value={currentContent}>
                                {!isEditMode && <StepSelectSurvey handleSelectedSurvey={handleSelectedSurvey} selectedSurvey={selectedSurvey} />}
                            </TabPanel>
                            <TabPanel index='#step2' value={currentContent}>
                                <StepPersonalizeSurvey
                                    handleNextStep={handleNextStep}
                                    handlePersonalizedSurvey={setPersonalizedSurvey}
                                    isEditMode={isEditMode}
                                    personalizedSurvey={personalizedSurvey}
                                />
                            </TabPanel>
                            <TabPanel index='#step3' value={currentContent}>
                                {selectedSurvey && (
                                    <StepSelectQuestion
                                        handleNextStep={handleNextStep}
                                        onSelectedQuestionsChange={setSelectedQuestions}
                                        selectedQuestions={selectedQuestions}
                                        selectedSurvey={selectedSurvey}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel index='#step4' value={currentContent}>
                                <StepSelectFilters
                                    handleNextStep={handleNextStep}
                                    onSelectedFiltersChange={setSelectedFilters}
                                    selectedFilters={selectedFilters}
                                    selectedQuestions={selectedQuestions}
                                />
                            </TabPanel>
                            <TabPanel index='#step5' value={currentContent}>
                                <StepConfigureHotAlerts
                                    configuratedHotAlerts={configuratedHotAlerts}
                                    handleNextStep={handleNextStep}
                                    onConfigureHotAlerts={setConfiguratedHotAlerts}
                                    selectedQuestions={selectedQuestions}
                                />
                            </TabPanel>
                            <TabPanel index='#step6' value={currentContent}>
                                {personalizedSurvey && (
                                    <StepDeployment
                                        configuratedHotAlerts={configuratedHotAlerts}
                                        editedSurvey={isEditMode ? { surveyId: surveyToEdit.surveyid, hotAlertConfigId } : undefined}
                                        onSuccess={handleClose}
                                        personalizedSurvey={personalizedSurvey}
                                        selectedFilters={selectedFilters}
                                        selectedQuestions={selectedQuestions}
                                    />
                                )}
                            </TabPanel>
                        </>
                    )}
                </BoxModalStyled>
            </Fade>
        </ModalStyled>
    );
};

export default CreateSurvey;
